/**
 * The fleet card's vehicle-type pill, and the two readouts that depend on it.
 *
 * A boat's altitude is the height of the water under it, give or take the GPS,
 * so "ALT 0 m" on its card says nothing and hides the number an operator of a
 * boat actually watches. Surface vehicles show speed and heading instead.
 */

import type { ReactNode } from 'react';

import { useDroneScalars, useDroneStore } from '@/stores/droneStore';
import type { DroneId } from '@/types/domain';

import { isSurfaceVehicle, knotsFromMs, vehicleKind, vehicleClassFor } from './vehicleKind';

function useAutopilot(droneId: DroneId): number | undefined {
  // The one field, not the whole HEARTBEAT, so the pill is not re-rendered by
  // every heartbeat's changing system status.
  return useDroneStore((state) => {
    const value = state.drones.get(droneId)?.telemetry.HEARTBEAT?.autopilot;
    return typeof value === 'number' ? value : undefined;
  });
}

export function DroneKindPill({ droneId }: { droneId: DroneId }): ReactNode {
  const { mavType } = useDroneScalars(droneId, ['mavType']);
  const autopilot = useAutopilot(droneId);
  const kind = vehicleKind(mavType, autopilot);

  return (
    <span
      title={kind.title}
      className="shrink-0 rounded-[99px] border border-[color:var(--border)] px-1.5 py-px label-mono text-[8px] text-[color:var(--muted-foreground-3)]"
    >
      {kind.label}
    </span>
  );
}

const READOUT = 'tnum text-[10.5px] text-[color:var(--muted-foreground)]';

export function DroneCardReadouts({ droneId }: { droneId: DroneId }): ReactNode {
  const { mavType, altRelative, groundSpeed, heading } = useDroneScalars(droneId, [
    'mavType',
    'altRelative',
    'groundSpeed',
    'heading',
  ]);

  if (isSurfaceVehicle(mavType)) {
    // Knots on the water, where every chart and every other boat speaks them.
    const boat = vehicleClassFor(mavType) === 'boat';
    const speed =
      groundSpeed === undefined
        ? 'SPD --'
        : boat
          ? `SPD ${knotsFromMs(groundSpeed).toFixed(1)} kn`
          : `SPD ${groundSpeed.toFixed(1)} m/s`;
    return (
      <>
        <span className={READOUT}>{speed}</span>
        <span className={READOUT}>
          {heading === undefined ? 'HDG --' : `HDG ${Math.round(heading).toString().padStart(3, '0')}`}
        </span>
      </>
    );
  }

  return (
    <>
      <span className={READOUT}>
        {altRelative === undefined ? 'ALT --' : `ALT ${altRelative.toFixed(0)} m`}
      </span>
      <span className={READOUT}>
        {groundSpeed === undefined ? 'GS --' : `GS ${groundSpeed.toFixed(1)}`}
      </span>
    </>
  );
}
